import type { AdvancedDateFilter } from '../components/ui/advanced-date-filter';

/**
 * Parse a YYYY-MM-DD date string as local midnight.
 * Using new Date("YYYY-MM-DD") parses as UTC midnight, which causes off-by-one
 * day display in timezones behind UTC (e.g. PST shows previous day).
 */
export function parseDateStringAsLocal(dateStr: string): Date {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return new Date(dateStr);
}

/**
 * Format a Date using its local date components as a YYYY-MM-DD string.
 * Avoids the timezone shift that toISOString() introduces (which uses UTC).
 */
export function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Format a Date as a start-of-day timestamp string (YYYY-MM-DDT00:00:00).
 * Used for lower-bound and exact-day filters (isEqualTo, isBetweenStart,
 * isOnOrAfter). These fields are GraphQL DateTime, which rejects a bare
 * YYYY-MM-DD with "DateTime cannot represent value", so the time must be sent.
 */
function formatLocalDateStartOfDay(date: Date): string {
  return `${formatLocalDate(date)}T00:00:00`;
}

/**
 * Format a Date as an end-of-day timestamp string (YYYY-MM-DDT23:59:59.999).
 * Used for upper-bound filters (isBeforeOrOn, isBetweenEnd) so the backend
 * includes records from the entire selected date rather than treating the
 * date-only string as midnight.
 */
function formatLocalDateEndOfDay(date: Date): string {
  return `${formatLocalDate(date)}T23:59:59.999`;
}


export interface DateRangeFilter {
  // Legacy format (for backward compatibility)
  start_date?: string;
  end_date?: string;
  exact_date?: string;
  
  // New backend format (camelCase)
  isInTheLast?: number;
  isInTheLastUnit?: 'days' | 'weeks' | 'months' | 'years';
  isEqualTo?: string;
  isBetweenStart?: string;
  isBetweenEnd?: string;
  isOnOrAfter?: string;
  isBeforeOrOn?: string;
  isAfter?: string;
  isBefore?: string;
}

/**
 * Converts an advanced date filter to the backend format expected by the API
 */
export function convertAdvancedDateFilterToBackend(filter: AdvancedDateFilter | undefined): DateRangeFilter | undefined {
  if (!filter || !filter.operator) {
    return undefined;
  }

  switch (filter.operator) {
    case 'is_in_the_last':
      if (filter.relativeValue && filter.relativeUnit) {
        return {
          isInTheLast: filter.relativeValue,
          isInTheLastUnit: filter.relativeUnit,
        };
      }
      break;

    case 'is_equal_to':
      if (filter.value && typeof filter.value === 'string') {
        const date = parseDateStringAsLocal(filter.value);
        if (isNaN(date.getTime())) {
          console.warn('Invalid date value:', filter.value);
          return undefined;
        }
        return {
          isEqualTo: formatLocalDateStartOfDay(date),
        };
      }
      break;

    case 'is_between':
      if (filter.value && typeof filter.value === 'object' && 'from' in filter.value && 'to' in filter.value) {
        const dateRange = filter.value as { from: Date; to: Date };
        if (dateRange.from && dateRange.to) {
          return {
            isBetweenStart: formatLocalDateStartOfDay(dateRange.from),
            isBetweenEnd: formatLocalDateEndOfDay(dateRange.to),
          };
        }
      }
      break;

    case 'is_on_or_after':
      if (filter.value && typeof filter.value === 'string') {
        const date = parseDateStringAsLocal(filter.value);
        if (isNaN(date.getTime())) {
          console.warn('Invalid date value:', filter.value);
          return undefined;
        }
        return {
          isOnOrAfter: formatLocalDateStartOfDay(date),
        };
      }
      break;

    case 'is_before_or_on':
      if (filter.value && typeof filter.value === 'string') {
        const date = parseDateStringAsLocal(filter.value);
        if (isNaN(date.getTime())) {
          console.warn('Invalid date value:', filter.value);
          return undefined;
        }
        return {
          isBeforeOrOn: formatLocalDateEndOfDay(date),
        };
      }
      break;

    case 'is_after':
      if (filter.value && typeof filter.value === 'string') {
        const date = parseDateStringAsLocal(filter.value);
        if (isNaN(date.getTime())) {
          console.warn('Invalid date value:', filter.value);
          return undefined;
        }
        return {
          isAfter: formatLocalDateEndOfDay(date),
        };
      }
      break;

    case 'is_before':
      if (filter.value && typeof filter.value === 'string') {
        const date = parseDateStringAsLocal(filter.value);
        if (isNaN(date.getTime())) {
          console.warn('Invalid date value:', filter.value);
          return undefined;
        }
        return {
          isBefore: formatLocalDate(date),
        };
      }
      break;
  }

  return undefined;
}


/**
 * Start of the local calendar day a date falls in.
 */
function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * End of the local calendar day a date falls in, to millisecond precision.
 */
function endOfLocalDay(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  );
}

/**
 * Moves a date back by a relative amount, in the units the "is in the last"
 * operator offers.
 */
function subtractRelative(
  from: Date,
  amount: number,
  unit: 'days' | 'weeks' | 'months' | 'years'
): Date {
  const result = new Date(from.getTime());

  switch (unit) {
    case 'days':
      result.setDate(result.getDate() - amount);
      break;
    case 'weeks':
      result.setDate(result.getDate() - amount * 7);
      break;
    case 'months':
      result.setMonth(result.getMonth() - amount);
      break;
    case 'years':
      result.setFullYear(result.getFullYear() - amount);
      break;
  }

  return result;
}

/**
 * Parses the date carried by a row being filtered.
 *
 * These are full timestamps off the API, so they keep their time of day — it is
 * what decides which local calendar day the row lands on.
 */
function parseSubjectDate(value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Parses a date a filter is bounded by.
 *
 * The date picker writes plain `YYYY-MM-DD`, which `new Date()` would read as
 * UTC midnight and so shift a day backwards anywhere behind UTC — the bound has
 * to be the local day the user picked.
 */
function parseBoundaryDate(value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : parseDateStringAsLocal(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Evaluates an advanced date filter against a single date, in the browser.
 *
 * `convertAdvancedDateFilterToBackend` above is the reference for what each
 * operator means; this is the same semantics for tables that hold every row in
 * memory and filter without re-querying. Bounds are inclusive at day
 * granularity — an upper bound covers the whole day selected, so a record
 * created at 4pm still matches "is before or on" that date, the way it does
 * server-side.
 *
 * A filter that is absent, has no operator, or is missing the operand its
 * operator needs matches everything: a half-filled filter popover should leave
 * the table alone rather than blank it. Once a filter *is* usable, a row whose
 * own date is missing or unparseable cannot satisfy it and drops out.
 */
export function matchesAdvancedDateFilter(
  value: string | Date | null | undefined,
  filter: AdvancedDateFilter | undefined
): boolean {
  if (!filter) {
    return true;
  }

  const subject = parseSubjectDate(value);
  const matches = (predicate: (date: Date) => boolean) =>
    subject ? predicate(subject) : false;

  if (!filter.operator) {
    // Legacy shape: bare start/end/exact fields with no operator.
    const exact = parseBoundaryDate(filter.exact_date);
    if (exact) {
      return matches(
        (date) => date >= startOfLocalDay(exact) && date <= endOfLocalDay(exact)
      );
    }

    const start = parseBoundaryDate(filter.startDate);
    const end = parseBoundaryDate(filter.endDate);
    if (!(start || end)) {
      return true;
    }

    return matches(
      (date) =>
        (!start || date >= startOfLocalDay(start)) &&
        (!end || date <= endOfLocalDay(end))
    );
  }

  // The operand for every single-date operator, or null while the popover has
  // an operator chosen but no date picked yet.
  const bound =
    typeof filter.value === 'string' ? parseBoundaryDate(filter.value) : null;

  switch (filter.operator) {
    case 'is_in_the_last': {
      if (!(filter.relativeValue && filter.relativeUnit)) {
        return true;
      }

      const cutoff = subtractRelative(
        new Date(),
        filter.relativeValue,
        filter.relativeUnit
      );
      return matches((date) => date >= cutoff);
    }

    case 'is_equal_to':
      if (!bound) {
        return true;
      }
      return matches(
        (date) => date >= startOfLocalDay(bound) && date <= endOfLocalDay(bound)
      );

    case 'is_between': {
      const range =
        filter.value && typeof filter.value === 'object'
          ? (filter.value as { from?: Date | string; to?: Date | string })
          : null;
      const from = parseBoundaryDate(range?.from);
      const to = parseBoundaryDate(range?.to);

      if (!(from && to)) {
        return true;
      }

      return matches(
        (date) => date >= startOfLocalDay(from) && date <= endOfLocalDay(to)
      );
    }

    case 'is_on_or_after':
      if (!bound) {
        return true;
      }
      return matches((date) => date >= startOfLocalDay(bound));

    case 'is_before_or_on':
      if (!bound) {
        return true;
      }
      return matches((date) => date <= endOfLocalDay(bound));

    case 'is_after':
      if (!bound) {
        return true;
      }
      return matches((date) => date > endOfLocalDay(bound));

    case 'is_before':
      if (!bound) {
        return true;
      }
      return matches((date) => date < startOfLocalDay(bound));

    default:
      return true;
  }
}
